import { useEffect, useRef, useState } from 'react';
import {
  apiRelatorios,
  type AnotacaoDaSecao,
  type Alinhamento,
  type Bloco,
  type Trecho,
} from '../lib/api.ts';
import { usarEhMaster } from '../lib/papel.ts';
import { htmlDoTextoColado, pareceMarcacaoDeChat } from '../lib/texto-colado';
import { avisar, avisarErro } from './Avisos.tsx';
import { Confirmar } from './Confirmar.tsx';
import { Icone } from './Icone.tsx';
import '../estilos/linha-rapida-2026.css';

/**
 * OS CAMPOS LIVRES da seção — 28/08/2026.
 *
 * > *"Uma espécie de 'anotação' dentro da seção […] que esses campos de escrita
 * > fosse igual um 'word', com uma espécie de faixa de opções."* — Richard
 *
 * > *"Gostaria que tivessem mais ferramentas, como por exemplo: colocar
 * > tópicos, tabelas, justificar parágrafos e etc."* — Richard, no mesmo dia
 *
 * ## ⚠️ O que se guarda NÃO é o HTML do editor
 *
 * A caixa é um `contentEditable` — é o que dá negrito, lista e tabela sem
 * biblioteca. **Mas o que ela produz nunca chega ao banco.** Ao sair do campo,
 * o Hub *lê* o HTML e monta blocos e trechos. Tag que ele não conhece vira
 * texto puro.
 *
 * ⭐ **O formato é a lista de permissões.** Colar um parágrafo do Word traz
 * fonte, corpo e cor junto; nada disso sobrevive à leitura, e o relatório sai
 * com a tipografia da KAIRÓS. Ver `api/src/lib/texto-formatado.ts`.
 */

/**
 * A faixa, em dois grupos — como a do Word que o Richard mostrou.
 *
 * ⚠️ **Só entra aqui o que o Word recebe IGUAL.** Cor de fonte, tamanho e
 * entrelinha existem na faixa dele e ficaram de fora de propósito: mudariam a
 * tipografia do relatório, que é da marca e não de quem escreve. **Botão que
 * promete e o papel não cumpre é pior que botão ausente.**
 */
const FONTE = [
  { chave: 'bold', rotulo: 'Negrito', mostra: 'N', estilo: { fontWeight: 700 } },
  { chave: 'italic', rotulo: 'Itálico', mostra: 'I', estilo: { fontStyle: 'italic' } },
  { chave: 'underline', rotulo: 'Sublinhado', mostra: 'S', estilo: { textDecoration: 'underline' } },
  {
    chave: 'strikeThrough',
    rotulo: 'Riscado',
    mostra: 'R',
    estilo: { textDecoration: 'line-through' },
  },
  { chave: 'grifo:amarelo', rotulo: 'Grifar em amarelo', mostra: 'G', estilo: { background: 'var(--grifo-amarelo)', color: '#191322' } },
  { chave: 'grifo:azul', rotulo: 'Grifar em azul', mostra: 'G', estilo: { background: 'var(--grifo-azul)', color: '#191322' } },
  { chave: 'grifo:verde', rotulo: 'Grifar em verde', mostra: 'G', estilo: { background: 'var(--grifo-verde)', color: '#191322' } },
  { chave: 'grifo:vermelho', rotulo: 'Grifar em vermelho', mostra: 'G', estilo: { background: 'var(--grifo-vermelho)', color: '#191322' } },
  { chave: 'subscript', rotulo: 'Subscrito', mostra: 'x₂', estilo: {} },
  { chave: 'superscript', rotulo: 'Sobrescrito', mostra: 'x²', estilo: {} },
] as const;

const PARAGRAFO = [
  { chave: 'insertUnorderedList', rotulo: 'Tópicos', mostra: '•—' },
  { chave: 'insertOrderedList', rotulo: 'Lista numerada', mostra: '1.' },
  { chave: 'justifyLeft', rotulo: 'Alinhar à esquerda', mostra: '⯇' },
  { chave: 'justifyCenter', rotulo: 'Centralizar', mostra: '⯈⯇' },
  { chave: 'justifyRight', rotulo: 'Alinhar à direita', mostra: '⯈' },
  { chave: 'justifyFull', rotulo: 'Justificar', mostra: '☰' },
] as const;

type Comando = (typeof FONTE)[number]['chave'] | (typeof PARAGRAFO)[number]['chave'];

/* ────────────────────────────────────────────────────────────────────────────
 * LER O EDITOR — e é aqui que a sanitização acontece
 * ────────────────────────────────────────────────────────────────────────── */

type Marcas = Omit<Trecho, 't'>;

/** As quatro cores de grifo — decisão dele, 31/08/2026. */
export type CorDeGrifo = 'amarelo' | 'azul' | 'verde' | 'vermelho';

const CORES_DE_GRIFO: CorDeGrifo[] = ['amarelo', 'azul', 'verde', 'vermelho'];

/** O valor do token, lido em tempo de execução. Cor não se repete no código. */
function corDoGrifo(cor: CorDeGrifo): string {
  return getComputedStyle(document.documentElement).getPropertyValue(`--grifo-${cor}`).trim();
}

/**
 * Normaliza uma cor para o `rgb(...)` que o navegador devolve.
 *
 * ⚠️ **Comparar hexadecimal com o que sai do DOM não funciona:** o navegador
 * guarda `#cfe8fb` e devolve `rgb(207, 232, 251)`. Normalizar os dois lados
 * pelo mesmo caminho — pintando num elemento solto e lendo de volta — é o que
 * torna a comparação confiável em qualquer navegador.
 */
function normalizar(cor: string): string {
  const d = document.createElement('span');
  d.style.backgroundColor = cor;
  return d.style.backgroundColor;
}

/**
 * Qual das quatro cores é este fundo — ou nulo, se for outra.
 *
 * ⚠️ **Nulo não é "sem grifo":** quem chama trata fundo desconhecido como
 * grifo amarelo, que é como o texto colado de fora se comportava antes das
 * cores existirem.
 */
function corConhecida(fundo: string): CorDeGrifo | null {
  const alvo = normalizar(fundo);
  for (const c of CORES_DE_GRIFO) {
    if (normalizar(corDoGrifo(c)) === alvo) return c;
  }
  return null;
}

const MARCA_POR_TAG: Record<string, keyof Marcas> = {
  b: 'n', strong: 'n',
  i: 'i', em: 'i',
  u: 's',
  s: 'r', strike: 'r', del: 'r',
  mark: 'g',
  sub: 'b',
  sup: 'c',
};

function marcasDe(el: HTMLElement, herdadas: Marcas): Marcas {
  const m: Marcas = { ...herdadas };
  const chave = MARCA_POR_TAG[el.tagName.toLowerCase()];
  if (chave) m[chave] = true;
  /* ⚠️ O grifo do navegador sai como fundo em `style`, e não como `<mark>`:
     `execCommand('hiliteColor')` não gera a tag. Ler o fundo é o que faz o
     botão de grifar funcionar em qualquer navegador.

     ⭐ **E desde 31/08 o fundo diz QUAL das quatro cores.** O navegador
     normaliza para `rgb(...)`, então a comparação é feita contra os tokens
     normalizados do mesmo jeito. ⚠️ **Fundo que não casa com nenhuma vira
     `true`** — texto colado de fora com cor própria continua sendo grifo, em
     amarelo, como era antes. */
  const fundo = el.style?.backgroundColor;
  if (fundo && fundo !== 'transparent' && !fundo.startsWith('rgba(0, 0, 0, 0')) {
    m.g = corConhecida(fundo) ?? true;
  }
  return m;
}

/**
 * Os formatos de imagem que a colagem aceita.
 *
 * ⚠️ **Os mesmos três que o botão 🖼 aceita**, e escritos aqui porque a colagem
 * precisa recusar ANTES de interceptar o evento — depois de interceptado, o
 * texto selecionado já foi consumido.
 */
const TIPOS_DE_IMAGEM = /^image\/(png|jpe?g|webp)$/i;

/**
 * ⛔ **AS QUEBRAS DE LINHA SUMIAM — 07/09/2026.**
 *
 * > *"Mesmo salvando, até o texto sai da formatação que eu deixei."* — Richard,
 * > três vezes
 *
 * ⚠️ **E não era marca de formatação nenhuma:** negrito, sublinhado, grifo e
 * alinhamento estavam sendo guardados corretamente — conferido no banco, no
 * texto real dele. O que sumia era a **quebra de linha dentro do parágrafo**,
 * o Shift+Enter.
 *
 * ⛔ `linhaDe()` percorre o parágrafo recolhendo **texto**, e `<br>` não tem
 * texto: contribuía com nada. Havia até um `filter(t => t.t !== '\n')` logo
 * adiante, apagando o que sobrasse. **Resultado: a redação dele voltava como um
 * bloco corrido justificado** — que é exatamente o que se vê na tela que ele
 * mandou.
 *
 * ⭐ **Cada quebra vira um PARÁGRAFO**, e não um `\n` dentro do trecho. É o que
 * o formato de blocos já sabe representar, e o que o `.docx` já sabe imprimir —
 * um `\n` no meio de um trecho obrigaria os três geradores a fatiar de novo.
 */
function linhasDe(no: Node, herdadas: Marcas = {}): Trecho[][] {
  const linhas: Trecho[][] = [[]];
  const andar = (n: Node, marcas: Marcas) => {
    if (n.nodeType === Node.TEXT_NODE) {
      const t = n.textContent ?? '';
      if (t) linhas[linhas.length - 1]!.push({ t, ...marcas });
      return;
    }
    if (n.nodeType !== Node.ELEMENT_NODE) return;
    const el = n as HTMLElement;
    if (el.tagName.toLowerCase() === 'br') {
      linhas.push([]);
      return;
    }
    const minhas = marcasDe(el, marcas);
    for (const filho of Array.from(el.childNodes)) andar(filho, minhas);
  };
  andar(no, herdadas);
  /*
   * ⛔ **A última linha vazia é RESÍDUO, não intenção — e sem esta poda o campo
   * CRESCIA a cada Salvar.**
   *
   * O `contentEditable` escreve `<div><br></div>` para uma linha em branco, e
   * `texto<br>` quando alguém quebra a linha no fim. Contando ingenuamente, o
   * primeiro vira **duas** linhas vazias e o segundo ganha uma vazia de brinde
   * — e como parágrafo vazio no MEIO é preservado de propósito, cada gravação
   * acrescentava mais um. Provado com duas figuras seguidas: voltavam com dois
   * parágrafos entre elas.
   *
   * ⚠️ **Só a ÚLTIMA, e só quando há outra linha.** Uma quebra no meio continua
   * valendo, e um `<div><br></div>` sozinho continua sendo a linha em branco
   * que alguém escreveu.
   */
  if (linhas.length > 1 && linhas[linhas.length - 1]!.length === 0) linhas.pop();
  return linhas;
}

/** Junta os trechos de um pedaço da árvore numa linha só. */
function linhaDe(no: Node, herdadas: Marcas = {}): Trecho[] {
  const saida: Trecho[] = [];
  const andar = (n: Node, marcas: Marcas) => {
    if (n.nodeType === Node.TEXT_NODE) {
      const t = n.textContent ?? '';
      if (t) saida.push({ t, ...marcas });
      return;
    }
    if (n.nodeType !== Node.ELEMENT_NODE) return;
    const el = n as HTMLElement;
    const minhas = marcasDe(el, marcas);
    for (const filho of Array.from(el.childNodes)) andar(filho, minhas);
  };
  andar(no, herdadas);
  return saida;
}

const alinhamentoDe = (el: HTMLElement): Alinhamento | undefined => {
  const a = el.style?.textAlign;
  if (a === 'center') return 'centro';
  if (a === 'right') return 'direita';
  if (a === 'justify') return 'justificado';
  if (a === 'left') return 'esquerda';
  return undefined;
};

/**
 * Lê o HTML da caixa e devolve os blocos.
 *
 * ⚠️ **É aqui que a sanitização acontece**, e é por isso que ela é de verdade:
 * o resultado só pode ser parágrafo, lista ou tabela, com trechos de sete
 * interruptores. Não há caminho pelo qual uma tag desconhecida atravesse — ela
 * simplesmente não tem para onde ir.
 */
/**
 * ⭐⭐ **FIXA NO SALVAR toda imagem que estiver na caixa — 07/09/2026.**
 *
 * > *"Preciso que, independente da imagem ou print que eu colocar nesses
 * > campos, precisa fixar (caso eu salve)."* — Richard, depois de duas
 * > tentativas minhas falharem
 *
 * ## ⛔ Por que interceptar a COLAGEM não bastava, duas vezes
 *
 * O registro do servidor foi o que decidiu: em noventa minutos de uso houve
 * **um** pedido — o Salvar dele — e **nenhum envio de imagem**. O caminho da
 * colagem dependia de adivinhar o que o navegador põe na área de
 * transferência, e cada origem de cópia põe uma coisa diferente. Eu errei a
 * adivinhação duas vezes seguidas.
 *
 * ⭐ **Esta rotina não adivinha nada.** Ela olha o que está na CAIXA na hora
 * de salvar — que é o que ele vê — e transforma em imagem da casa tudo o que
 * o navegador tiver posto ali, tenha vindo de colagem, de arrasto ou de
 * qualquer outro caminho que exista hoje ou venha a existir.
 *
 * ## ⚠️ A regra do `CLAUDE.md` fica INTEIRA, e por dentro fica mais forte
 *
 * A proibição é a imagem que mora num servidor de terceiro: *"o relatório
 * pediria a figura a um servidor de terceiro toda vez que fosse aberto"*.
 *
 * - **`blob:` e `data:` são bytes LOCAIS**, do próprio navegador — não há
 *   terceiro. Sobem para o Hub e viram imagem da casa. **O motivo da regra
 *   não se aplica a elas.**
 * - **`http:` e `https:` continuam recusados**, e agora **em voz alta**:
 *   antes sumiam calados, que é a metade do defeito que ele relatou.
 *
 * ⚠️ **Sobe TUDO antes de gravar, e em fila.** Ao gravar, o servidor apaga do
 * disco toda imagem que o corpo salvo não citar. Uma imagem que subisse
 * depois — ou em paralelo — seria apagada pela gravação da anterior.
 */
export async function fixarImagensSoltas(
raiz: HTMLElement,
/**
 * Quem sobe os bytes e devolve os blocos.
 *
 * ⚠️ **Entra por parâmetro para esta rotina poder ser PROVADA fora do
 * navegador** — foi assim que o defeito da figura aninhada foi pego. Rotina
 * que só se testa clicando é rotina que se conserta por tentativa.
 */
enviar: (arquivo: File) => Promise<{ blocos: Bloco[] }>,
): Promise<string[]> {
  const soltas = Array.from(raiz.querySelectorAll('img')).filter(
    (i) => !i.getAttribute('data-imagem'),
  );
  if (soltas.length === 0) return [];

  const recados: string[] = [];
  let deFora = 0;

  for (const img of soltas) {
    const src = img.getAttribute('src') ?? '';

    /* ⛔ Servidor de terceiro: a regra da casa, dita em voz alta. */
    if (/^https?:/i.test(src)) {
      img.remove();
      deFora += 1;
      continue;
    }

    /*
     * ⛔ **NADA MAIS SAI EM SILÊNCIO — 07/09/2026, quinta rodada.**
     *
     * A versão anterior removia calada qualquer endereço que não fosse `blob:`
     * nem `data:` — e ele viu o print entrar no texto e sumir ao salvar **sem
     * uma palavra**. O registro do servidor confirmou: um Salvar, nenhum envio.
     *
     * ⭐ **Agora TUDO que não é site é tentado**, seja qual for o endereço, e o
     * que não der certo é DITO. Endereço que o navegador não sabe buscar cai no
     * `catch` e vira recado com a origem — em vez de a figura evaporar entre o
     * clique e o recarregar.
     */
    try {
      const bytes = await fetch(src).then((r) => r.blob());
      if (!bytes.size) throw new Error('vazia');
      const tipo = TIPOS_DE_IMAGEM.test(bytes.type) ? bytes.type : 'image/png';
      /* ⚠️ Nome genérico: nome de arquivo NÃO carrega dado de investigado. */
      const arquivo = new File([bytes], 'imagem-colada.png', { type: tipo });
      const r = await enviar(arquivo);
      const figura = document.createElement('div');
      figura.innerHTML = escreverNoEditor(r.blocos);
      /* Troca a imagem solta pela figura da casa, no MESMO lugar do texto. */
      img.replaceWith(...Array.from(figura.childNodes));
    } catch {
      /* ⚠️ Falhou: a imagem SAI da caixa e o recado DIZ de onde ela veio.
         Deixá-la ali faria o Salvar seguinte tentar de novo e o texto nunca
         fechar — e sumir calada é o defeito que ele relatou cinco vezes. */
      img.remove();
      recados.push(
        `Uma imagem não pôde ser anexada e ficou de fora (origem: ${
          src.slice(0, 24) || 'desconhecida'
        }…). Tente o botão 🖼.`,
      );
    }
  }

  /*
   * ⚠️ **Confirma o que deu certo, e não só o que deu errado.** Cinco rodadas
   * de "coloquei e sumiu" vieram, em boa parte, de a tela nunca dizer se a
   * figura tinha sido de fato anexada.
   */
  const anexadas = soltas.length - deFora - recados.length;
  if (anexadas > 0) recados.push(`${anexadas} imagem(ns) anexada(s) ao campo.`);

  if (deFora > 0) {
    recados.push(
      `${deFora} imagem(ns) veio(vieram) de um site e não entra(m) no relatório: ` +
        'o documento pediria a figura ao site toda vez que fosse aberto. ' +
        'Salve a imagem no computador e cole o arquivo.',
    );
  }
  return recados;
}

/**
 * ⛔ **Sobe para o primeiro nível toda figura que ficou aninhada — 07/09/2026.**
 *
 * `lerDoEditor` percorre **só os filhos diretos** da caixa. Uma figura dentro de
 * um `<div>` de parágrafo é invisível para ele: o `<div>` vai para `linhaDe()`,
 * que recolhe **texto** — e imagem não é texto. **A figura some sem erro
 * nenhum.**
 *
 * ⚠️ **E aninhar é o caso NORMAL, não a exceção.** A figura entra onde o cursor
 * está, e o cursor quase sempre está no meio de um parágrafo já escrito. Foi
 * exatamente isto que o Richard viu: colou, salvou, recarregou, e só o texto
 * ficou. **Mesmo com o envio ao servidor funcionando, ela se perdia aqui.**
 *
 * ⚠️ **A figura vai para DEPOIS do parágrafo que a continha**, e não para o
 * meio dele: partir o parágrafo em dois seria o Hub reescrevendo a redação de
 * quem escreveu. Sai um pouco abaixo de onde foi colada, e sai.
 *
 * ⚠️ **Mexe no DOM vivo de propósito.** Quem escreve vê a figura assentar no
 * lugar em que ela será gravada — em vez de descobrir a diferença ao recarregar.
 */
function desaninharFiguras(raiz: HTMLElement): void {
  /* Teto de voltas: DOM inesperado não pode virar laço infinito na tela. */
  for (let volta = 0; volta < 50; volta += 1) {
    const presa = Array.from(raiz.querySelectorAll('figure')).find(
      (f) => f.parentElement !== raiz,
    );
    if (!presa) return;
    let bloco: HTMLElement = presa;
    while (bloco.parentElement && bloco.parentElement !== raiz) bloco = bloco.parentElement;

    /*
     * ⛔ **O QUE VEM DEPOIS DA FIGURA SOBE JUNTO — 08/09/2026.**
     *
     * > *"Os demais campos da aba Relatórios, com exceção do campo
     * > investigados, não está sendo possível inserir textos após as
     * > imagens."* — Richard
     *
     * ⛔ **E o defeito era pior do que o relato:** o texto escrito depois da
     * imagem **não sumia — ele mudava de lugar**. Ao subir só a figura, tudo o
     * que estava depois dela ficava dentro do parágrafo antigo, que continua
     * **antes**. Medido na bancada, com o caminho real da colagem:
     *
     * | Ele escrevia | O Hub gravava |
     * |---|---|
     * | antes · IMAGEM · texto depois | antes · **texto depois** · IMAGEM |
     *
     * ⚠️ **E, colando no meio de um parágrafo, o texto ainda era FUNDIDO** com
     * o que havia antes: *"antes da imagem texto depois da imagem"* num
     * parágrafo só. Redação embaralhada, sem erro nenhum aparecer.
     *
     * ⭐ **A correção é partir o bloco em três** — o que vinha antes fica, a
     * figura sobe, e o que vinha depois vira um parágrafo próprio **depois
     * dela**. É a ordem que quem escreve vê na tela, e é a única que preserva a
     * intenção.
     *
     * ⚠️ **Por que só agora:** enquanto a figura vinha do banco, ela já nascia
     * no primeiro nível e nada disto acontecia. O defeito vivia **na sessão em
     * que a imagem é colada** — e é por isso que o campo dos investigados, cujo
     * conteúdo já estava gravado, parecia o único a funcionar.
     */
    const resto = document.createRange();
    resto.setStartAfter(presa);
    resto.setEnd(bloco, bloco.childNodes.length);
    const depois = resto.extractContents();

    /*
     * ⚠️ **A linha de CORTESIA não vira parágrafo em branco.**
     *
     * `escreverNoEditor` põe um `<div><br></div>` logo depois de toda figura,
     * para haver onde clicar — a figura é `contenteditable="false"` e o cursor
     * não entra nela. Esse `div` vem no resto e viraria um parágrafo vazio
     * **entre a imagem e o texto**, que ninguém escreveu.
     *
     * ⚠️ **Só o PRIMEIRO, e só se estiver vazio.** Se ele digitou ali, a linha
     * não está vazia e é redação. E um segundo vazio é separação de gente — a
     * leitura preserva o vazio do meio de propósito.
     */
    const semConteudo = (n: Node): boolean =>
      n.nodeType === Node.ELEMENT_NODE &&
      !(n.textContent ?? '').trim() &&
      !(n as HTMLElement).querySelector('figure, img, table, ul, ol');

    /*
     * ⚠️ **A cortesia pode estar ENTERRADA.** Colando de um editor externo, a
     * figura vem dentro de `<span>` dentro de `<div>` — e o `<div><br></div>`
     * sai no extrato **debaixo** dessa mesma casca. Olhar só o primeiro filho
     * da raiz deixaria o parágrafo vazio passar, e ele apareceria entre a
     * imagem e o texto. Desço pela cadeia dos primeiros filhos até achá-la.
     */
    let alvo: Node | null = depois.firstChild;
    for (let fundo = 0; alvo && fundo < 10; fundo += 1) {
      if (semConteudo(alvo)) {
        alvo.parentNode?.removeChild(alvo);
        break;
      }
      alvo = alvo.firstChild;
    }

    raiz.insertBefore(presa, bloco.nextSibling);

    /*
     * ⚠️ **Só entra se houver conteúdo de verdade.** A colagem deixa um
     * `<div><br></div>` de cortesia depois da figura; promovê-lo a parágrafo
     * vazio acrescentaria uma linha em branco que ninguém escreveu — e a
     * leitura já poda o resíduo do fim.
     */
    if ((depois.textContent ?? '').trim() || depois.querySelector('figure, img, table, ul, ol')) {
      const caixa = document.createElement('div');
      caixa.appendChild(depois);
      raiz.insertBefore(caixa, presa.nextSibling);
    }

    /*
     * ⚠️ **O bloco que SÓ continha a figura some — senão vira linha em branco.**
     *
     * Colando numa linha vazia, o parágrafo hospedeiro fica oco depois de a
     * figura sair. Deixá-lo acrescentaria um parágrafo em branco **antes** da
     * imagem que ninguém escreveu — e a leitura preserva o vazio do meio de
     * propósito, por ser separação de gente. Aqui não é: é resíduo da mudança.
     *
     * ⚠️ **Só quando ficou REALMENTE vazio.** Texto antes da figura é redação,
     * e continua onde estava.
     */
    if (!(bloco.textContent ?? '').trim() && !bloco.querySelector('figure, img, table, ul, ol')) {
      bloco.remove();
    }
  }
}

export function lerDoEditor(raiz: HTMLElement): Bloco[] {
  desaninharFiguras(raiz);
  const blocos: Bloco[] = [];
  /* Texto solto na raiz, sem `<div>` em volta — o `contentEditable` deixa
     assim a primeira linha digitada. */
  let soltos: Trecho[] = [];

  const fecharSoltos = () => {
    if (soltos.length > 0) {
      blocos.push({ especie: 'paragrafo', trechos: soltos });
      soltos = [];
    }
  };

  for (const no of Array.from(raiz.childNodes)) {
    if (no.nodeType === Node.TEXT_NODE) {
      const t = no.textContent ?? '';
      if (t.trim()) soltos.push({ t });
      continue;
    }
    if (no.nodeType !== Node.ELEMENT_NODE) continue;
    const el = no as HTMLElement;
    const nome = el.tagName.toLowerCase();

    if (nome === 'br') {
      fecharSoltos();
      /* Linha em branco: parágrafo sem trecho nenhum. É a separação que quem
         escreveu usou, e apagá-la seria o Hub reescrevendo a redação. */
      blocos.push({ especie: 'paragrafo', trechos: [] });
      continue;
    }

    if (nome === 'ul' || nome === 'ol') {
      fecharSoltos();
      const itens = Array.from(el.querySelectorAll(':scope > li')).map((li) => linhaDe(li));
      if (itens.length > 0) {
        blocos.push({
          especie: 'lista',
          ...(nome === 'ol' ? { ordenada: true as const } : {}),
          itens,
        });
      }
      continue;
    }

    if (nome === 'img') {
      fecharSoltos();
      const id = el.getAttribute('data-imagem');
      /* ⚠️ Imagem sem o nosso `data-imagem` é imagem colada de fora — um
         `<img src="https://…">` arrastado de outra página. **Não entra**: o
         relatório apontaria para um servidor de terceiro, e o método vazaria
         no primeiro pedido que o Word fizesse ao abrir o documento. */
      if (id) {
        const legenda = el.getAttribute('data-legenda') ?? undefined;
        blocos.push({ especie: 'imagem', imagemId: id, ...(legenda ? { legenda } : {}) });
      }
      continue;
    }

    if (nome === 'figure') {
      fecharSoltos();
      const img = el.querySelector('img[data-imagem]');
      const id = img?.getAttribute('data-imagem');
      if (id) {
        const legenda = el.querySelector('figcaption')?.textContent?.trim();
        /*
         * ⚠️ **A largura é lida do estilo e ARREDONDADA para inteiro**, porque
         * arrastar produz fração e o servidor só aceita inteiro entre 10 e 100.
         * Fora da faixa, some — e a figura volta ao tamanho natural, que é o
         * comportamento de quem nunca escolheu tamanho nenhum.
         */
        const larguraCrua = Number.parseFloat(el.style?.width ?? '');
        const larg = Number.isFinite(larguraCrua) ? Math.round(larguraCrua) : null;
        blocos.push({
          especie: 'imagem',
          imagemId: id,
          ...(legenda ? { legenda } : {}),
          ...(larg !== null && larg >= 10 && larg <= 100 ? { larguraEmPorcento: larg } : {}),
        });
      }
      continue;
    }

    if (nome === 'table') {
      fecharSoltos();
      const linhas = Array.from(el.querySelectorAll('tr')).map((tr) =>
        Array.from(tr.querySelectorAll('th, td')).map((c) => linhaDe(c)),
      );
      if (linhas.length > 0) blocos.push({ especie: 'tabela', linhas });
      continue;
    }

    /* Bloco de texto: `<div>` é o que o `contentEditable` cria a cada Enter. */
    fecharSoltos();
    const alinhamento = alinhamentoDe(el);
    /*
     * ⚠️ **Uma quebra de linha vira um parágrafo, e o alinhamento vai em todos.**
     * Sem repetir o alinhamento, a segunda metade de um parágrafo justificado
     * voltaria alinhada à esquerda — e a pessoa veria o texto "se desarrumar"
     * exatamente no ponto em que apertou Shift+Enter.
     */
    for (const trechos of linhasDe(el)) {
      blocos.push({
        especie: 'paragrafo',
        ...(alinhamento ? { alinhamento } : {}),
        trechos: trechos.filter((t) => t.t !== '\n'),
      });
    }
  }
  fecharSoltos();

  /* ⚠️ Parágrafo vazio no FIM é resíduo do editor, não intenção — o
     `contentEditable` deixa um `<div><br></div>` sobrando. No meio ele fica. */
  while (
    blocos.length > 0 &&
    blocos[blocos.length - 1]!.especie === 'paragrafo' &&
    (blocos[blocos.length - 1] as { trechos: Trecho[] }).trechos.length === 0
  ) {
    blocos.pop();
  }
  return blocos;
}

/* ────────────────────────────────────────────────────────────────────────────
 * ESCREVER NO EDITOR
 * ────────────────────────────────────────────────────────────────────────── */

const escapar = (t: string) =>
  t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function linhaEmHtml(l: Trecho[]): string {
  if (l.length === 0) return '<br>';
  return l
    .map((t) => {
      let html = escapar(t.t);
      if (t.n) html = `<b>${html}</b>`;
      if (t.i) html = `<i>${html}</i>`;
      if (t.s) html = `<u>${html}</u>`;
      if (t.r) html = `<s>${html}</s>`;
      if (t.b) html = `<sub>${html}</sub>`;
      if (t.c) html = `<sup>${html}</sup>`;
      /* ⚠️ **A cor volta no `style`, e não só a tag.** Sem ela, reabrir o campo
         devolveria tudo em amarelo — e a cor que ele escolheu se perderia no
         primeiro fechamento da tela. `true` é o formato antigo: sai amarelo. */
      if (t.g) {
        const cor = t.g === true ? 'amarelo' : t.g;
        /* ⛔ **O valor RESOLVIDO, e não `var(--grifo-azul)`.** O navegador não
           devolve a função `var()` em `element.style.backgroundColor`, então a
           leitura não reconheceria a cor de volta — e ela viraria amarelo na
           primeira reabertura. Por isso o token é resolvido aqui. */
        html = `<mark style="background-color: ${escapar(corDoGrifo(cor))}">${html}</mark>`;
      }
      return html;
    })
    .join('');
}

const CSS_DO_ALINHAMENTO: Record<Alinhamento, string> = {
  esquerda: 'left',
  centro: 'center',
  direita: 'right',
  justificado: 'justify',
};

export function escreverNoEditor(corpo: Bloco[]): string {
  return corpo
    .map((b, i) => {
      if (b.especie === 'lista') {
        const tag = b.ordenada ? 'ol' : 'ul';
        return `<${tag}>${b.itens.map((i) => `<li>${linhaEmHtml(i)}</li>`).join('')}</${tag}>`;
      }
      if (b.especie === 'tabela') {
        const linhas = b.linhas
          .map(
            (linha, i) =>
              `<tr>${linha
                .map((c) => `<${i === 0 ? 'th' : 'td'}>${linhaEmHtml(c)}</${i === 0 ? 'th' : 'td'}>`)
                .join('')}</tr>`,
          )
          .join('');
        return `<table>${linhas}</table>`;
      }
      if (b.especie === 'imagem') {
        /* ⚠️ `contenteditable="false"` na figura: sem isso o cursor entra
           DENTRO dela e quem digitar apaga a imagem sem perceber. A legenda
           fica editável à parte. */
        const legenda = b.legenda ? escapar(b.legenda) : '';
        /*
         * ⭐ **A largura escolhida vai na FIGURA, não na imagem — 07/09/2026.**
         * A legenda precisa acompanhar a figura ao encolher; presa só na
         * imagem, ela continuaria com a largura inteira e o conjunto sairia
         * torto.
         *
         * ⚠️ **Ausente NÃO vira 100%.** Sem escolha, a figura fica do tamanho
         * natural do arquivo, limitado pela caixa — que é como tudo o que já
         * está escrito se comporta hoje.
         */
        const larg = b.larguraEmPorcento ? ` style="width: ${b.larguraEmPorcento}%"` : '';
        /*
         * ⛔ **A "nota" só existe quando HÁ nota — 07/09/2026.**
         *
         * > *"Não precisa ter 'nota' nas imagens que eu colocar."* — Richard
         *
         * ⚠️ **E a caixa vazia não era só feia: ela CONVIDAVA a digitar dentro.**
         * Uma linha editável logo abaixo da figura, sempre presente, parece a
         * continuação do texto — e o parágrafo que ele quis escrever acabou
         * gravado como legenda da imagem. Foi o que aconteceu no caso real.
         *
         * ⚠️ **A legenda continua existindo no formato**, e é usada: página de
         * PDF convertida sai com "documento — página 3". O que sai é a caixa
         * vazia oferecida sem pedido.
         */
        /*
         * ⭐ **A nota VOLTOU, e opcional — 07/09/2026, segundo pedido dele:**
         * *"pode voltar, e ter a 'nota' (opcional) nas imagens que eu colocar"*.
         *
         * ⚠️ **O `data-nota` é o que faz a caixa vazia parecer vazia.** Sem o
         * texto de apoio, uma linha editável em branco sob a figura parece a
         * continuação do parágrafo — e foi assim que a redação dele acabou
         * gravada como legenda da imagem. Agora ela se anuncia.
         */
        const nota =
          `<figcaption contenteditable="true" data-nota="Nota (opcional)">${legenda}</figcaption>`;
        return (
          `<figure contenteditable="false"${larg}>` +
          `<img data-imagem="${escapar(b.imagemId)}" src="/api/relatorios/imagens/${escapar(b.imagemId)}" alt="${legenda || 'Imagem anexada'}">` +
          nota +
          /*
           * ⭐ **O ✕ no canto superior direito, e a alça no inferior — 07/09/2026.**
           *
           * ⛔ **A fileira "Menor · Maior · Apagar" saiu, e ele recusou os
           * dois:** *"não gostei dos botões dessa forma. Além do botão clicável
           * para redimensionar"*. Botão de tamanho em passos não é
           * redimensionar — *"eu gostaria que de fato eu conseguisse
           * redimensionar o tamanho da imagem. Atualmente não é livre."*
           *
           * ⚠️ **O ✕ existe porque a figura é `contenteditable="false"`** — o
           * cursor não entra nela, e por isso a tecla Delete não a alcança. Sem
           * este botão não há como apagar uma imagem.
           *
           * ⚠️ **Nada disto vira bloco na leitura:** `lerDoEditor` lê da figura
           * apenas a imagem e a nota.
           */
          `<button type="button" class="apagar-a-imagem" contenteditable="false" data-imagem-acao="apagar" title="Apagar esta imagem" aria-label="Apagar esta imagem">✕</button>` +
          `<span class="alca-da-imagem" contenteditable="false" aria-hidden="true" title="Arraste para redimensionar"></span>` +
          `</figure>` +
          /*
           * ⛔ **DEPOIS DA FIGURA PRECISA HAVER ONDE ESCREVER — 07/09/2026.**
           *
           * > *"Após colar a imagem, eu não consigo escrever mais (continuar
           * > escrevendo depois de qualquer imagem)."* — Richard
           *
           * ⚠️ **É consequência direta de a figura ser
           * `contenteditable="false"`.** O cursor não entra nela; se não houver
           * um parágrafo logo depois, **não existe lugar nenhum** para onde ele
           * possa ir, e o campo fica travado a partir dali.
           *
           * ⚠️ **Só quando o que vem depois NÃO é parágrafo** — figura no fim,
           * ou duas figuras seguidas. Havendo texto adiante, ele já é o lugar,
           * e uma linha vazia extra seria o Hub mexendo na redação.
           *
           * ⭐ **E não suja o documento:** a leitura descarta parágrafo vazio no
           * FIM, que é resíduo do editor. O do MEIO ela preserva, porque é
           * separação escrita por gente.
           */
          (corpo[i + 1]?.especie === 'paragrafo' ? '' : '<div><br></div>')
        );
      }
      const estilo = b.alinhamento ? ` style="text-align: ${CSS_DO_ALINHAMENTO[b.alinhamento]}"` : '';
      return `<div${estilo}>${linhaEmHtml(b.trechos)}</div>`;
    })
    .join('');
}

const vazio = (corpo: Bloco[]) => {
  const temTexto = (l: Trecho[]) => l.some((t) => t.t.trim().length > 0);
  return !corpo.some((b) =>
    b.especie === 'paragrafo'
      ? temTexto(b.trechos)
      : b.especie === 'lista'
        ? b.itens.some(temTexto)
        : /* ⚠️ **Imagem CONTA como conteúdo, mesmo sem legenda.** Um campo com
             um print e nada escrito não é campo em branco — e campo em branco
             não vai ao documento. */
          b.especie === 'imagem'
          ? true
          : b.linhas.some((l) => l.some(temTexto)),
  );
};

/* ────────────────────────────────────────────────────────────────────────── */

export function Anotacoes({
  relatorioId,
  secaoId,
  anotacoes,
  travado,
  aoMudar,
  investigadoId,
  rotulo,
  tituloSugerido,
}: {
  relatorioId: string;
  secaoId: string;
  anotacoes: AnotacaoDaSecao[];
  travado: boolean;
  aoMudar: () => Promise<void> | void;
  /**
   * ⭐ **Quando vem, este bloco é de UMA pessoa — 31/08/2026.**
   *
   * > *"Na seção INVESTIGADOS, incluir o botão abaixo de cada investigado."*
   *
   * ⚠️ **O componente é o MESMO nos dois usos**, de propósito: dois editores
   * para o mesmo campo divergiriam na primeira correção feita só num deles.
   * O que muda é a lista que ele recebe e o `investigadoId` que ele carimba.
   */
  investigadoId?: string;
  /** O texto do botão. Sob um investigado ele diz de quem é o campo. */
  rotulo?: string;
  /** ⭐ 16/10/2026: o que o campo de título pede (no Anexos, o nome do anexo). */
  tituloSugerido?: string;
}) {
  const ehMaster = usarEhMaster();
  const [criando, setCriando] = useState(false);

  async function acrescentar() {
    if (criando) return;
    setCriando(true);
    try {
      await apiRelatorios.criarAnotacao(relatorioId, secaoId, undefined, investigadoId);
      await aoMudar();
    } catch (e) {
      avisarErro(e);
    } finally {
      setCriando(false);
    }
  }

  return (
    <div className="anotacoes">
      {anotacoes.map((a) => (
        <Anotacao
          key={a.id}
          relatorioId={relatorioId}
          anotacao={a}
          travado={travado}
          podeApagar={ehMaster}
          aoMudar={aoMudar}
          tituloSugerido={tituloSugerido}
        />
      ))}

      {!travado && (
        <button type="button" className="botao-secundario" onClick={acrescentar} disabled={criando}>
          {rotulo ?? '+ Acrescentar campo de escrita'}
        </button>
      )}
    </div>
  );
}

export function Anotacao({
  relatorioId,
  anotacao,
  travado,
  podeApagar,
  semTitulo,
  aoMudar,
  tituloSugerido,
}: {
  relatorioId: string;
  anotacao: AnotacaoDaSecao;
  travado: boolean;
  podeApagar: boolean;
  tituloSugerido?: string;
  /**
   * ⭐ **Esconde o campo do título — 23/09/2026.**
   *
   * ⚠️ **O título de um campo SAI IMPRESSO no documento.** No passo "O texto
   * que voltou" o campo não é uma anotação com nome: é o texto do tópico. Um
   * campo de título ali convida a escrever "texto que voltou da IA" — e isso
   * chegaria ao cliente como subtítulo. O campo continua existindo no banco,
   * vazio; só não se oferece.
   */
  semTitulo?: boolean;
  aoMudar: () => Promise<void> | void;
}) {
  const caixa = useRef<HTMLDivElement>(null);
  const [titulo, setTitulo] = useState(anotacao.titulo ?? '');
  const [estado, setEstado] = useState<'parado' | 'gravando' | 'gravado'>('parado');
  /** Há texto digitado e ainda não salvo. É o que acende o aviso e o botão. */
  const [sujo, setSujo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [apagando, setApagando] = useState(false);
  /**
   * ⭐ O que a conferência achou no texto deste campo — 11/09/2026.
   *
   * ⛔ **O campo livre era o terceiro caminho sem trava.** Foi por ele que
   * entraram, no PDF do primeiro caso real, a legenda *"imagem extraída do
   * google street view"* e *"em consulta pela empresa na rede mundial de
   * computadores"* — método, no documento do cliente.
   */
  const [achados, setAchados] = useState<
    Array<{ gravidade: 'grave' | 'atencao'; aviso: string; trecho: string }>
  >([]);
  /** ⭐ O cursor está dentro de uma tabela? Decide os botões dela — 11/09/2026. */
  const [naTabela, setNaTabela] = useState(false);
  const arquivo = useRef<HTMLInputElement>(null);

  /*
   * ⚠️ **O HTML entra UMA vez, e não a cada tecla.** Reescrever o conteúdo de um
   * `contentEditable` a cada mudança de estado devolve o cursor para o começo do
   * campo — quem estivesse escrevendo veria as letras saindo ao contrário. Por
   * isso a caixa não é controlada por React: ela é semeada aqui e lida no blur.
   */
  useEffect(() => {
    if (caixa.current) caixa.current.innerHTML = escreverNoEditor(anotacao.corpo);
  }, [anotacao.id]);

  /*
   * ⚠️ **O navegador pergunta antes de fechar com texto não salvo.**
   *
   * ⛔ É o preço da gravação manual, e ele precisa de rede: sem isto, fechar a
   * aba com um parágrafo escrito perde o parágrafo em silêncio — que é o
   * oposto do que o botão Salvar existe para evitar.
   */
  useEffect(() => {
    if (!sujo) return;
    const avisar = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [sujo]);

  /**
   * ⭐ **O botão SALVAR — 31/08/2026, pedido dele.**
   *
   * > *"Precisa ter um botão 'salvar' para não ter o risco de, acidentalmente,
   * > excluir o que estiver escrito e não conseguir recuperar (assim, se eu não
   * > clicar em 'salvar', o texto retoma como estava da última vez)."*
   *
   * ⛔ **A gravação no BLUR saiu, e é o que dá sentido ao pedido.** Antes, sair
   * do campo gravava — inclusive um apagão acidental, que virava definitivo
   * sem ninguém clicar em nada. Agora o que não for salvo é descartado ao
   * recarregar, e o "Descartar" devolve o texto da última gravação.
   *
   * ⚠️ **O preço da escolha está na cara:** quem escreve e fecha a aba sem
   * salvar perde. Por isso a marca de alteração pendente aparece o tempo todo e
   * o navegador pergunta antes de sair.
   */
  async function gravar() {
    if (!caixa.current || travado) return;
    setEstado('gravando');
    /* ⚠️ Antes de LER: o que subir vira figura da casa e entra no corpo. */
    const recados = await fixarImagensSoltas(caixa.current, (a) =>
      apiRelatorios.anexarNaAnotacao(relatorioId, anotacao.id, a),
    );
    for (const r of recados) avisar(r);
    const corpo = lerDoEditor(caixa.current);
    try {
      const r = await apiRelatorios.escreverAnotacao(relatorioId, anotacao.id, {
        titulo: titulo.trim() || null,
        corpo,
      });
      setAchados(r.achados);
      setEstado('gravado');
      setSujo(false);
      window.setTimeout(() => setEstado('parado'), 1600);
    } catch (e) {
      setEstado('parado');
      avisarErro(e);
      return;
    }
    /*
     * ⛔ **Depois de gravar, a tela RELÊ o relatório — 19/09/2026.** O servidor
     * gravava, e a tela não relia: o índice seguia dizendo "ainda em branco", e
     * ao sair do tópico e voltar o campo renascia com o texto da abertura da
     * página — vazio. Foi o que ele viu no caso real (*"o texto não está
     * salvando nesse tópico"*), e o pior vinha depois: salvar por cima daquela
     * tela velha gravaria o texto velho. A caixa não é reescrita pela releitura
     * (ela é semeada só quando muda de campo), e o cursor fica onde estava.
     */
    await aoMudar();
  }

  /*
   * ⭐ **A janelinha da casa, não a do navegador — 19/09/2026.** Era o único
   * apagar do Hub com o `confirm()` nativo: sem a marca, sem dizer o que se
   * perde, e com o foco no "OK" — o Enter por reflexo apagava.
   */
  async function apagar() {
    try {
      await apiRelatorios.apagarAnotacao(relatorioId, anotacao.id);
      await aoMudar();
    } catch (e) {
      avisarErro(e);
    }
  }

  /**
   * Aplica um comando ao trecho selecionado.
   *
   * ⚠️ **`execCommand` está descontinuado e continua sendo a escolha certa
   * aqui.** O substituto exige manipular seleção e intervalo à mão — trabalho
   * grande e cheio de casos de borda para entregar negrito e lista. As
   * alternativas prontas são bibliotecas de editor, e esta casa tem **duas
   * dependências no total**. ⭐ **Se um navegador deixar de suportá-lo, o texto
   * continua sendo escrito; só os botões param.**
   */
  function comandar(chave: Comando) {
    caixa.current?.focus();
    /* Pede TAGS em vez de `style`: `<b>` é o que a leitura entende sem depender
       de folha de estilo. O grifo é a exceção, e por isso vai pelo fundo. */
    document.execCommand('styleWithCSS', false, 'false');
    if (chave.startsWith('grifo:')) {
      /* ⚠️ A cor sai do TOKEN, e não de um hexadecimal aqui — cor escrita em
         dois lugares um dia diverge. E os tokens de grifo NÃO mudam com o tema,
         justamente para que a leitura reconheça de volta o que foi pintado. */
      const cor = corDoGrifo(chave.slice('grifo:'.length) as CorDeGrifo);
      document.execCommand('hiliteColor', false, cor || 'yellow');
      return;
    }
    document.execCommand(chave, false);
  }

  /**
   * Insere uma tabela onde o cursor está.
   *
   * ⚠️ **Três por três, e sem diálogo para escolher o tamanho.** Linha e coluna
   * se acrescentam digitando: `Tab` na última célula cria a linha seguinte, que
   * é como o Word faz. Um diálogo pedindo dimensões antes de existir conteúdo é
   * pedir uma decisão que ninguém tem como tomar ainda.
   *
   * ⚠️ **A primeira linha é o CABEÇALHO** — vai em `<th>`, e é ela que o Word
   * repete quando a tabela vira a página.
   */
  /**
   * ⭐ **A tabela nasce 3×3 e CRESCE — 11/09/2026.**
   *
   * ⚠️ **O tamanho inicial deixou de ser o tamanho final.** Antes era o único
   * que existia; agora é só por onde se começa, e os botões ao lado (que
   * aparecem quando o cursor está dentro dela) acrescentam e tiram linha e
   * coluna. ⭐ **Tab na última célula cria a linha seguinte**, como em qualquer
   * editor de texto.
   */
  function inserirTabela() {
    caixa.current?.focus();
    const celulas = (tag: string, n: number) => Array.from({ length: n }, () => `<${tag}><br></${tag}>`).join('');
    const html =
      `<table><tr>${celulas('th', 3)}</tr>` +
      `<tr>${celulas('td', 3)}</tr>` +
      `<tr>${celulas('td', 3)}</tr></table><div><br></div>`;
    document.execCommand('insertHTML', false, html);
    setSujo(true);
    /* ⚠️ O cursor cai na primeira célula, e os botões precisam saber disso. */
    window.setTimeout(conferirTabela, 0);
  }

  /* ────────────────────────────────────────────────────────────────────────
   * A TABELA LIVRE — 11/09/2026
   *
   * > *"Tem um ícone de inserir uma tabela 3x3. Mas essa opção deveria ser
   * > inserir uma tabela onde eu posso acrescentar linha ou coluna, ou seja,
   * > tabela livre."* — Richard
   *
   * ⭐ **O formato guardado SEMPRE aceitou tabela de qualquer tamanho** —
   * `linhas: z.array(z.array(linha).max(12)).max(200)` em `texto-formatado.ts`.
   * A limitação era só da inserção: o botão criava 3×3 e não havia como mexer.
   *
   * ⛔ **Os tetos são do FORMATO, não da tela — e por isso a tela os respeita.**
   * Deixar passar a 13ª coluna faria o SALVAR quebrar depois de o trabalho estar
   * escrito. Recusar antes, dizendo por quê, é a única forma honesta.
   * ────────────────────────────────────────────────────────────────────────── */

  const MAXIMO_DE_COLUNAS = 12;
  const MAXIMO_DE_LINHAS = 200;

  /** A célula onde o cursor está, quando ele está numa tabela DESTA caixa. */
  function celulaDoCursor(): HTMLTableCellElement | null {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    let no: Node | null = sel.getRangeAt(0).startContainer;
    while (no && no !== caixa.current) {
      if (no instanceof HTMLTableCellElement) return no;
      no = no.parentNode;
    }
    return null;
  }

  /**
   * ⚠️ **Mudança no DOM NÃO dispara `onInput`.** O `execCommand` dispara; mexer
   * na árvore à mão, não. Sem esta chamada o aviso de alteração pendente não
   * acende e o trabalho se perde ao fechar — em silêncio.
   */
  function mexeuNaTabela() {
    setSujo(true);
    conferirTabela();
  }

  /** Uma célula vazia, no tipo certo para a linha. */
  const celulaVazia = (cabecalho: boolean) => {
    const c = document.createElement(cabecalho ? 'th' : 'td');
    c.appendChild(document.createElement('br'));
    return c;
  };

  function acrescentarLinha() {
    const celula = celulaDoCursor();
    const linha = celula?.parentElement as HTMLTableRowElement | undefined;
    const tabela = linha?.closest('table');
    if (!celula || !linha || !tabela) return;
    if (tabela.rows.length >= MAXIMO_DE_LINHAS) {
      return avisarErro(`A tabela chegou ao teto de ${MAXIMO_DE_LINHAS} linhas.`);
    }
    const nova = tabela.insertRow(linha.rowIndex + 1);
    /* ⚠️ Sempre `td`: só a PRIMEIRA linha é cabeçalho, e ela nunca se duplica. */
    for (let i = 0; i < linha.cells.length; i += 1) nova.appendChild(celulaVazia(false));
    porCursorEm(nova.cells[0]);
    mexeuNaTabela();
  }

  function acrescentarColuna() {
    const celula = celulaDoCursor();
    const tabela = celula?.closest('table');
    if (!celula || !tabela) return;
    const quantas = (tabela.rows[0]?.cells.length ?? 0) + 1;
    if (quantas > MAXIMO_DE_COLUNAS) {
      return avisarErro(
        `A tabela chegou ao teto de ${MAXIMO_DE_COLUNAS} colunas. ` +
          'Acima disso o documento não conseguiria imprimi-la legível numa folha A4.',
      );
    }
    const onde = celula.cellIndex + 1;
    for (const linha of Array.from(tabela.rows)) {
      /* ⚠️ **O tipo vem da LINHA, não da célula ao lado.** Na primeira linha a
         célula nova é `th`; nas outras, `td`. */
      const nova = celulaVazia(linha.rowIndex === 0);
      linha.insertBefore(nova, linha.cells[onde] ?? null);
    }
    porCursorEm(tabela.rows[celula.parentElement instanceof HTMLTableRowElement ? celula.parentElement.rowIndex : 0]?.cells[onde]);
    mexeuNaTabela();
  }

  function removerLinha() {
    const celula = celulaDoCursor();
    const linha = celula?.parentElement as HTMLTableRowElement | undefined;
    const tabela = linha?.closest('table');
    if (!celula || !linha || !tabela) return;
    /* ⛔ **O CABEÇALHO não se apaga.** A primeira linha é o cabeçalho por
       convenção do formato — é ela que o Word repete ao virar a folha. Sem ela
       a tabela sairia sem cabeçalho ou com um inventado. */
    if (linha.rowIndex === 0) {
      return avisarErro(
        'A primeira linha é o cabeçalho da tabela e não se apaga — é ela que se repete ' +
          'quando a tabela vira a página. Para tirar a tabela inteira, apague-a selecionando.',
      );
    }
    const proxima = tabela.rows[linha.rowIndex + 1] ?? tabela.rows[linha.rowIndex - 1];
    tabela.deleteRow(linha.rowIndex);
    porCursorEm(proxima?.cells[0]);
    mexeuNaTabela();
  }

  function removerColuna() {
    const celula = celulaDoCursor();
    const tabela = celula?.closest('table');
    if (!celula || !tabela) return;
    if ((tabela.rows[0]?.cells.length ?? 0) <= 1) {
      return avisarErro('A tabela ficaria sem nenhuma coluna. Apague a tabela inteira.');
    }
    const onde = celula.cellIndex;
    for (const linha of Array.from(tabela.rows)) {
      if (linha.cells[onde]) linha.deleteCell(onde);
    }
    porCursorEm(tabela.rows[0]?.cells[Math.max(0, onde - 1)]);
    mexeuNaTabela();
  }

  /** Põe o cursor dentro de uma célula, no começo. */
  function porCursorEm(celula: HTMLTableCellElement | undefined) {
    if (!celula) return;
    const faixa = document.createRange();
    faixa.selectNodeContents(celula);
    faixa.collapse(true);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(faixa);
    caixa.current?.focus();
  }

  /**
   * ⭐ **Tab anda de célula em célula, e na última cria uma linha.**
   *
   * ⚠️ **É a convenção que todo editor de texto tem**, e quem escreve tabela
   * espera dela: sem isto, acrescentar linha exigiria tirar a mão do teclado a
   * cada linha. ⛔ E sem interceptar, o Tab do navegador **sai da caixa** e o
   * foco vai parar no botão seguinte, no meio da frase.
   */
  function aoTeclar(e: React.KeyboardEvent) {
    if (e.key !== 'Tab') return;
    const celula = celulaDoCursor();
    if (!celula) return;
    const linha = celula.parentElement as HTMLTableRowElement;
    const tabela = linha.closest('table');
    if (!tabela) return;
    e.preventDefault();

    if (e.shiftKey) {
      const anterior =
        linha.cells[celula.cellIndex - 1] ??
        tabela.rows[linha.rowIndex - 1]?.cells[tabela.rows[linha.rowIndex - 1]!.cells.length - 1];
      return porCursorEm(anterior);
    }
    const proxima = linha.cells[celula.cellIndex + 1] ?? tabela.rows[linha.rowIndex + 1]?.cells[0];
    if (proxima) return porCursorEm(proxima);
    /* Última célula da última linha: cria a próxima. */
    acrescentarLinha();
  }

  /** Acende os botões da tabela quando o cursor entra numa. */
  function conferirTabela() {
    setNaTabela(celulaDoCursor() !== null);
  }


  /**
   * Anexa um arquivo e insere a figura onde o cursor está.
   *
   * ⚠️ **Insere no CURSOR, e não no fim do texto.** Empurrar a figura para o
   * fim poria o print longe do parágrafo que ele ilustra — e quem anexou teria
   * de arrastá-la de volta, num campo onde arrastar imagem é justamente o que
   * dá errado.
   *
   * ⛔ **PDF é CONVERTIDO, não anexado.** Um `.docx` não embute página de PDF.
   * A tela diz isso ANTES, e diz de novo quando o arquivo tem mais páginas que
   * o teto — converter as trinta primeiras e calar faria quem enviou acreditar
   * que o documento inteiro entrou.
   */
  async function anexar(arquivo: File) {
    const dentro = caixa.current;
    if (!dentro) return;

    /*
     * ⛔ **O LUGAR DO CURSOR É GUARDADO ANTES DO ENVIO — 07/09/2026.**
     *
     * ⚠️ **Era esta a causa de "não consigo incluir a imagem".** O envio demora,
     * e durante a espera o cursor se perde — o campo perde o foco, ou a pessoa
     * clica em outro lugar. `execCommand('insertHTML')` depende de haver
     * seleção viva: **sem ela não insere nada e não reclama.**
     *
     * ⭐ **E o registro do servidor provou exatamente isso:** às 18h20 uma
     * imagem SUBIU (o arquivo de 94 KB está no disco), e o texto salvo
     * continuou apontando só para a figura antiga. O envio funcionava; a
     * inserção é que não acontecia — e a gravação seguinte apagava do banco a
     * imagem que ninguém citava.
     *
     * ⭐ **Guardar o intervalo e inseri-lo à mão não depende de foco nenhum.**
     * E quando não há cursor — porque nunca houve clique no campo —, a figura
     * vai para o FIM, que é onde alguém que não escolheu lugar espera achá-la.
     */
    const selecao = window.getSelection();
    let ondeEstava: Range | null = null;
    if (selecao && selecao.rangeCount > 0) {
      const r = selecao.getRangeAt(0);
      if (dentro.contains(r.commonAncestorContainer)) ondeEstava = r.cloneRange();
    }

    setEnviando(true);
    try {
      const r = await apiRelatorios.anexarNaAnotacao(relatorioId, anotacao.id, arquivo);
      const molde = document.createElement('div');
      molde.innerHTML = escreverNoEditor(r.blocos) + '<div><br></div>';
      const pedaco = document.createDocumentFragment();
      for (const no of Array.from(molde.childNodes)) pedaco.appendChild(no);

      if (ondeEstava) {
        ondeEstava.deleteContents();
        ondeEstava.insertNode(pedaco);
      } else {
        dentro.appendChild(pedaco);
      }
      setSujo(true);
      await gravar();
      if (r.cortou) {
        avisar(
          `O PDF tem ${r.paginas} páginas e entraram as ${r.teto} primeiras. ` +
            'Se precisar do resto, envie o arquivo em partes.',
        );
      }
    } catch (e) {
      avisarErro(e);
    } finally {
      setEnviando(false);
    }
  }

  /**
   * ⭐ **A COLAGEM DE PRINT — 07/09/2026, e é um DEFEITO que ele relatou duas
   * vezes.**
   *
   * > *"Ao colar uma imagem e clicar no botão salvar ela não ficou gravada no
   * > corpo do texto."* — Richard
   *
   * ## ⛔ Por que ela sumia, e por que a regra da casa NÃO estava errada
   *
   * Quem punha a figura na tela era o navegador: ela existia só dentro daquela
   * janela, e nada era enviado ao servidor. Ao salvar, `lerDoEditor` descarta
   * `<img>` sem `data-imagem` — e a colada não tinha marcador nenhum.
   *
   * ⚠️ **E o descarte acontecia em DOIS lugares.** Com o cursor numa linha
   * vazia, a figura ao menos chegava à conferência do marcador. Com o cursor no
   * MEIO de um parágrafo — o caso normal —, ela nem chegava lá: `linhaDe()`
   * recolhe só TEXTO, e imagem não é texto. Sumia antes de qualquer
   * conferência.
   *
   * ⚠️ **E a caixa não é redesenhada depois de salvar.** A figura continuava na
   * tela, o Hub dizia "gravado", e a perda só aparecia ao reabrir o relatório.
   * Na prática o sistema mentia.
   *
   * ⭐ **A correção de entendimento que resolve tudo:** um print colado da área
   * de transferência **não é** a "imagem de servidor de terceiro" que o
   * `CLAUDE.md` proíbe. É um arquivo novo, inteiro, dentro da máquina — como se
   * a pessoa tivesse acabado de salvá-lo em PNG. O que a regra proíbe é a
   * imagem ARRASTADA de uma página da internet, que fica hospedada fora e que o
   * Word iria buscar lá toda vez que o documento fosse aberto. **Essa continua
   * proibida**, e nada aqui a afrouxa: o caminho novo entrega os BYTES ao Hub
   * pela mesma porta do botão 🖼, e a figura nasce com o marcador da casa.
   */
  /**
   * ⚠️ **Uma colagem por vez, em fila.** Ao gravar, o servidor apaga do disco
   * toda imagem que o corpo salvo não citar. Dois Ctrl+V em 200 ms fariam a
   * gravação do primeiro **apagar o arquivo do segundo** — perda definitiva,
   * com a figura quebrada na tela e sumida em silêncio no documento.
   */
  const fila = useRef<Promise<void>>(Promise.resolve());

  /**
   * ⭐ **A ALÇA QUE ARRASTA — 07/09/2026.**
   *
   * > *"Ter a possibilidade de eu conseguir redimensionar de forma livre (o
   * > tamanho) imagem dentro do próprio campo no qual eu estiver escrevendo."*
   *
   * ⚠️ **Alça própria, e NÃO a do navegador.** A figura é
   * `contenteditable="false"` desde que existe — sem isso o cursor entra dentro
   * dela e quem digita apaga a imagem sem perceber. Essa mesma proteção desliga
   * as alças que o navegador desenharia sozinho, então elas se desenham aqui.
   *
   * ⚠️ **Arrastar mexe só no ESTILO da figura.** Quem grava é o botão Salvar,
   * como em todo o resto do campo — e `lerDoEditor` recolhe a largura do estilo
   * na hora de montar o bloco. Gravar a cada pixel arrastado encheria o banco
   * de versões de um gesto.
   *
   * ⚠️ **`setSujo` na hora**, senão a barra de "alterações não salvas" não
   * aparece e a pessoa sai da tela achando que o tamanho ficou.
   */
  /**
   * O clique no ✕ de uma figura — 07/09/2026.
   *
   * ⚠️ **Só tira da tela; quem grava é o botão Salvar**, como todo o resto do
   * campo. E o servidor apaga o arquivo do disco sozinho quando o corpo salvo
   * deixa de citá-lo — não há segunda porta para o apagamento.
   *
   * ⚠️ **`setSujo` na hora**, senão a barra de "alterações não salvas" não
   * aparece e a pessoa sai da tela achando que apagou.
   */
  function aoClicarNaCaixa(evento: React.MouseEvent<HTMLDivElement>) {
    const alvo = evento.target as HTMLElement;
    if (alvo.dataset?.imagemAcao !== 'apagar' || travado) return;
    evento.preventDefault();
    alvo.closest('figure')?.remove();
    setSujo(true);
  }

  function aoPuxarAlca(evento: React.PointerEvent<HTMLDivElement>) {
    const alvo = evento.target as HTMLElement;
    if (!alvo.classList?.contains('alca-da-imagem')) return;
    const figura = alvo.closest('figure') as HTMLElement | null;
    const dentro = caixa.current;
    if (!figura || !dentro || travado) return;

    evento.preventDefault();

    const util = dentro.clientWidth;
    const inicial = figura.getBoundingClientRect().width;
    const x0 = evento.clientX;

    /*
     * ⛔ **Ouve a JANELA, e não a alça — 07/09/2026.**
     *
     * A versão anterior capturava o ponteiro na própria alça e escutava nela.
     * Ele arrastou e nada aconteceu: *"atualmente não é livre"*. Alça de 14 px
     * dentro de uma figura travada contra edição é frágil demais para ser o
     * único ouvinte — basta o ponteiro sair dela por um instante.
     *
     * ⭐ **A janela inteira ouve enquanto o botão estiver pressionado**, que é
     * como todo redimensionamento por arrasto funciona. Solta, para de ouvir.
     */
    const mover = (e: PointerEvent) => {
      /* ⚠️ Piso 10 e teto 100, os mesmos da alça, do botão e do servidor. */
      const bruto = ((inicial + (e.clientX - x0)) / util) * 100;
      figura.style.width = `${Math.round(Math.min(100, Math.max(10, bruto)))}%`;
    };
    const soltar = () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', soltar);
      setSujo(true);
    };
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    window.addEventListener('pointercancel', soltar);
  }

  function aoColar(evento: React.ClipboardEvent<HTMLDivElement>) {
    if (travado) return;
    const dados = evento.clipboardData;
    if (!dados) return;

    /*
     * ⭐⭐ **A MARCAÇÃO DO CHAT VIRA FORMATAÇÃO — 23/09/2026.**
     *
     * O texto que volta do Claude ou do GPT usa `**negrito**` e `- marcador`.
     * Colado cru, os asteriscos entravam no campo e **saíam impressos no
     * documento do cliente** — foi o que apareceu no PDF do caso 2026-003.
     *
     * ⚠️ **Só quando NÃO há HTML no clipboard.** Texto copiado do Word já vem
     * formatado, e o editor sabe lê-lo; converter ali trocaria formatação real
     * por uma leitura de asteriscos que não existem.
     */
    const tiposColados = Array.from(dados.types ?? []);
    const puro = dados.getData('text/plain');
    if (!tiposColados.includes('text/html') && puro && pareceMarcacaoDeChat(puro)) {
      evento.preventDefault();
      document.execCommand('insertHTML', false, htmlDoTextoColado(puro));
      setSujo(true);
      return;
    }

    /*
     * ⛔ **QUALQUER arquivo de imagem é aceito — 07/09/2026, terceira rodada.**
     *
     * As duas versões anteriores tentavam decidir *quando* a colagem era de
     * imagem, olhando `files` numa e o `text/html` na outra. As duas erraram, e
     * o registro do servidor provou: dois Salvar dele, **nenhum envio**.
     *
     * ⭐ **E o motivo de a tela parecer não fazer nada é do navegador:** colar
     * um print num campo de texto **não insere nada** no Chrome. Ele só insere
     * figura quando a área de transferência traz HTML com `<img>`. Sem alguém
     * interceptar, a colagem é um gesto sem efeito — foi isso que ele viu
     * quando disse *"não estou conseguindo incluir a imagem que eu quero"*.
     *
     * ⚠️ **A regra do Excel deixou de vetar, e passou a conviver.** Copiando
     * uma planilha vem tabela E bitmap; antes o bitmap fazia a colagem desistir
     * inteira. Agora o texto entra pelo caminho de sempre **e** a imagem sobe.
     * Vir demais é recuperável — some com um clique no ✕. Vir de menos é o
     * defeito que ele relatou três vezes.
     *
     * ⛔ **E o print subia DUAS VEZES — 15/09/2026.** O Chrome e o Firefox
     * descrevem o mesmo arquivo em `files` E em `items`; somar as duas listas
     * dava dois `File` com os mesmos bytes, dois envios e duas figuras. As
     * duas listas são o MESMO conteúdo: lê `files`, e `items` só quando
     * `files` vem vazio.
     */
    const doArquivo = Array.from(dados.files ?? []);
    const daLista =
      doArquivo.length > 0
        ? doArquivo
        : Array.from(dados.items ?? [])
            .filter((it) => it.kind === 'file')
            .map((it) => it.getAsFile())
            .filter((f): f is File => f !== null);
    const imagens = daLista.filter((f) => f.type.startsWith('image/'));

    if (imagens.length === 0) {
      /*
       * ⚠️ **Quando a colagem TRAZ figura e não traz arquivo, a tela DIZ o que
       * veio.** É o caso da imagem copiada de um site: o navegador manda só o
       * endereço dela. Sumir calado aqui foi metade do defeito das três
       * rodadas — e um recado com os tipos recebidos transforma o próximo teste
       * dele em prova, em vez de tentativa.
       */
      const tipos = Array.from(dados.types ?? []);
      if (tipos.includes('text/html') && /<img/i.test(dados.getData('text/html'))) {
        avisar(
          'Essa imagem veio de um site, e o Hub recebeu só o endereço dela — ' +
            'não o arquivo. Salve a imagem no computador e cole o arquivo, ' +
            'ou use o botão 🖼.',
        );
      }
      return;
    }

    const aceitos = imagens.filter((f) => TIPOS_DE_IMAGEM.test(f.type));
    if (aceitos.length === 0) {
      avisar(
        `Esse formato de imagem não entra (${imagens[0]!.type || 'desconhecido'}). ` +
          'Cole um PNG, JPG ou WEBP.',
      );
      return;
    }

    evento.preventDefault();
    for (const a of aceitos) {
      fila.current = fila.current.then(() => anexar(a)).catch(() => undefined);
    }
  }

  const botao = (
    chave: Comando,
    rotulo: string,
    mostra: string,
    estilo?: React.CSSProperties,
  ) => (
    <button
      key={chave}
      type="button"
      title={rotulo}
      aria-label={rotulo}
      /* ⚠️ `onMouseDown` com `preventDefault`, e não `onClick`: o clique tira o
         foco da caixa e **perde a seleção** antes de o comando rodar — o
         negrito não sairia em nada. */
      onMouseDown={(e) => {
        e.preventDefault();
        comandar(chave);
      }}
      style={estilo}
    >
      {mostra}
    </button>
  );

  return (
    <div className="anotacao">
      <div className="anotacao-topo">
        {semTitulo ? (
          <span className="anotacao-titulo-vago" />
        ) : (
          <input
            className="anotacao-titulo"
            value={titulo}
            onChange={(e) => {
              setTitulo(e.target.value);
              setSujo(true);
            }}
            placeholder={tituloSugerido ?? 'Título da anotação (opcional)'}
            maxLength={120}
            disabled={travado}
          />
        )}
        {podeApagar && !travado && (
          <button type="button" className="imv-bt fantasma p" onClick={() => setApagando(true)}>
            <Icone nome="lixeira" tamanho={13} /> Apagar
          </button>
        )}
      </div>

      {/* ⚠️ **Dois grupos, como a faixa do Word que ele mostrou** — o que muda a
          LETRA de um lado, o que muda o PARÁGRAFO do outro. Numa fileira só, sete
          botões de fonte e seis de parágrafo viram treze quadradinhos iguais, e
          o olho procura um por um. */}
      {!travado && (
        <div className="anotacao-faixa" role="toolbar" aria-label="Formatação do texto">
          <div className="faixa-grupo" aria-label="Fonte">
            {FONTE.map((m) => botao(m.chave, m.rotulo, m.mostra, m.estilo))}
          </div>
          <div className="faixa-grupo" aria-label="Parágrafo">
            {PARAGRAFO.map((m) => botao(m.chave, m.rotulo, m.mostra))}
            <button
              type="button"
              title="Inserir tabela — depois dá para acrescentar linha e coluna"
              aria-label="Inserir tabela"
              onMouseDown={(e) => {
                e.preventDefault();
                inserirTabela();
              }}
            >
              ▦
            </button>
          </div>

          {/*
            ⭐⭐ **OS BOTÕES DA TABELA SÓ EXISTEM DENTRO DELA — 11/09/2026.**

            ⚠️ **Controle que não se aplica não fica cinza: não aparece.** Quatro
            botões desabilitados na faixa o tempo todo seriam quatro coisas a
            ignorar em todo campo de escrita — e o campo de escrita é o lugar
            onde a faixa já é a parte mais cheia da tela.

            ⛔ **Eles NÃO usam `execCommand`.** Acrescentar linha e coluna é
            mexer na árvore à mão, e por isso cada um chama `mexeuNaTabela()`:
            mudança no DOM não dispara `onInput`, e sem isso o aviso de
            alteração pendente não acende.
          */}
          {naTabela && (
            <div className="faixa-grupo faixa-da-tabela" aria-label="Tabela">
              <span className="faixa-rotulo">tabela</span>
              {(
                [
                  ['+ linha', 'Acrescentar linha abaixo', acrescentarLinha],
                  ['− linha', 'Apagar esta linha', removerLinha],
                  ['+ coluna', 'Acrescentar coluna à direita', acrescentarColuna],
                  ['− coluna', 'Apagar esta coluna', removerColuna],
                ] as const
              ).map(([rotulo, titulo, acao]) => (
                <button
                  key={rotulo}
                  type="button"
                  className="botao-da-tabela"
                  title={titulo}
                  aria-label={titulo}
                  onMouseDown={(e) => {
                    /* ⚠️ `preventDefault` no `mouseDown` guarda o cursor: sem
                       ele o clique tira o foco da caixa e a seleção se perde
                       antes de a ação rodar. Mesma razão dos outros botões. */
                    e.preventDefault();
                    acao();
                  }}
                >
                  {rotulo}
                </button>
              ))}
            </div>
          )}
          <div className="faixa-grupo" aria-label="Anexar">
            {/*
              ⭐ **O ANEXAR passou a ter NOME — 10/09/2026.**

              ⛔ Ele era só o ícone 🖼 no fim da faixa, e num campo cheio de
              ícones de formatação lia-se como enfeite. O Richard abriu o
              capítulo **Anexos** — cujo propósito inteiro é anexar — e
              concluiu que ali só dava para escrever: *"não somente ter a
              opção de escrever... precisa ter a opção que eu possa anexar um
              PDF ou uma imagem"*. A função existia e estava ligada desde
              07/09; o que faltava era ela se anunciar.

              ⚠️ **Palavra, não ícone maior.** Ícone sozinho depende de quem
              olha adivinhar o desenho; a palavra "Anexar" não depende.
            */}
            <button
              type="button"
              className="botao-anexar"
              title="Anexar imagem, print ou PDF — o PDF entra convertido em imagem, página por página"
              aria-label="Anexar imagem, print ou PDF"
              disabled={enviando}
              onMouseDown={(e) => {
                e.preventDefault();
                arquivo.current?.click();
              }}
            >
              {enviando ? 'enviando…' : '📎 Anexar PDF ou imagem'}
            </button>
          </div>
          <span className="anotacao-estado">
            {estado === 'gravando' ? 'gravando…' : estado === 'gravado' ? 'gravado' : ''}
          </span>
        </div>
      )}

      {/*
        ⚠️ **O CAMPO DE ARQUIVO fica escondido, e é o botão da faixa que o
        aciona** — um `<input type="file">` cru no meio da faixa não se estiliza
        e quebra o alinhamento dos outros botões.
      */}
      <input
        ref={arquivo}
        type="file"
        accept="image/png,image/jpeg,application/pdf"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          /* Limpa o campo: sem isso, anexar o MESMO arquivo duas vezes seguidas
             não dispara nada, porque o valor não mudou. */
          e.target.value = '';
          if (f) void anexar(f);
        }}
      />

      {/*
        ⚠️ **O AVISO DO MÉTODO fica ao lado do botão, e não escondido numa
        ajuda.** É a regra inegociável da casa — *"o cliente recebe o resultado,
        não o método"* — e o print é o jeito mais fácil de quebrá-la.

        ⭐ **Avisa, não bloqueia**, e a razão é que os dois casos existem: print
        da tela da Assertiva mostra a **ferramenta**; print da Receita, da
        matrícula ou da junta mostra o **registro**, e esse pode (refinamento de
        16/08/2026). **O Hub não distingue os dois numa imagem** — bloquear
        impediria o legítimo, calar deixaria o método vazar sem um aviso.
      */}
      {!travado && (
        <p className="ajuda-alerta ajuda-solta">
          O que for anexado <strong>vai ao documento que o cliente recebe</strong>. Print de
          registro oficial — Receita, matrícula, junta — pode. Print da tela de uma
          ferramenta de pesquisa mostra o método, e não. <strong>PDF entra convertido em
          imagem, página por página</strong>: o Word não embute o arquivo.
        </p>
      )}

      <div
        ref={caixa}
        className="anotacao-caixa"
        contentEditable={!travado}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={titulo || 'Campo de escrita'}
        /*
         * ⭐ **O corretor, DECLARADO AQUI TAMBÉM — 08/09/2026.**
         *
         * ⚠️ **Não é redundância com o `<body spellcheck="true">`:** a herança
         * cobre o Hub inteiro e o campo que alguém criar amanhã; esta linha
         * cobre **este** campo, que é o único de escrita rica e o único onde o
         * Richard relatou o corretor não funcionar.
         *
         * ⛔ **Ela existia em 07/09 e eu a REMOVI em 08/09 achando que a
         * herança bastava — sem nunca ter visto a versão explícita
         * funcionando.** Tirar o que não se sabe se funcionava é trocar um
         * defeito conhecido por um desconhecido.
         *
         * ⚠️ **`lang` junto, e não só no `<html>`.** Num campo com texto de um
         * caso, é aqui que a língua do conteúdo se afirma.
         */
        spellCheck
        lang="pt-BR"
        /* ⛔ **NÃO grava mais aqui.** Ver o comentário de `gravar()`: o blur
           gravava até 31/08/2026, e era isso que tornava um apagão acidental
           definitivo. Agora ele só acende o aviso de alteração pendente. */
        onInput={() => {
          setSujo(true);
          /* ⚠️ Digitar pode entrar ou sair de uma tabela — apagar a última
             célula, colar por cima. Os botões seguem o cursor. */
          conferirTabela();
        }}
        /* ⭐ Tab anda de célula em célula e cria linha na última — ver `aoTeclar`. */
        onKeyDown={aoTeclar}
        /* ⚠️ **`onKeyUp` além do `onInput`**: as setas movem o cursor sem mudar
           o texto, e sair de uma tabela com a seta para baixo precisa apagar os
           botões dela. */
        onKeyUp={conferirTabela}
        /* ⭐ A colagem de print — ver `aoColar`. */
        onPaste={aoColar}
        /* ⭐ A alça que redimensiona — ver `aoPuxarAlca`. */
        onPointerDown={aoPuxarAlca}
        /* ⭐ O ✕ que apaga a figura — ver `aoClicarNaCaixa`. */
        onClick={(e) => {
          aoClicarNaCaixa(e);
          conferirTabela();
        }}
        /* ⚠️ Sair da caixa apaga os botões da tabela: eles operam sobre um
           cursor que já não está lá. */
        onBlur={() => setNaTabela(false)}
      />

      {/*
        ⚠️ **O aviso e os dois botões só aparecem quando há o que salvar.**
        Barra de ação permanente vira paisagem; que aparece quando muda alguma
        coisa, se lê.
      */}
      {sujo && !travado && (
        <div className="anotacao-salvar">
          <span className="ajuda">Alterações não salvas.</span>
          <button type="button" className="imv-bt primario p" onClick={() => void gravar()}>
            <Icone nome="check" tamanho={13} /> {estado === 'gravando' ? 'Salvando…' : 'Salvar'}
          </button>
          <button
            type="button"
            className="imv-bt fantasma p"
            onClick={() => {
              /* Devolve o texto da última gravação — é o "retoma como estava"
                 que ele pediu. A caixa não é controlada pelo React, então o
                 conteúdo é semeado de novo à mão. */
              if (caixa.current) caixa.current.innerHTML = escreverNoEditor(anotacao.corpo);
              setTitulo(anotacao.titulo ?? '');
              setSujo(false);
            }}
          >
            Descartar alterações
          </button>
        </div>
      )}

      {/*
        ⛔ **Avisa e não bloqueia.** O texto já está gravado quando isto aparece
        — quem decide o que vale no documento assinado é ele. É a mesma régua da
        conferência da seção, agora no terceiro caminho: o campo livre.
      */}
      {achados.length > 0 && (
        <div className="aviso-atencao">
          <p className="rotulo">A conferência achou isto no texto deste campo</p>
          <ul className="atos-lidos">
            {achados.map((a, i) => (
              /* ⚠️ O índice na chave — 12/09/2026: dois achados podem ter
                 aviso e trecho iguais, e chave repetida faz o React trocar um
                 aviso pelo outro. */
              <li key={`${i}-${a.aviso}`}>
                <strong>{a.aviso}</strong>
                {/* ⚠️ O recorte é o que faz o aviso ser procurável: sem ele,
                    "há método no texto" manda reler o campo inteiro. */}
                <br />
                <span className="ajuda">{a.trecho}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {vazio(anotacao.corpo) && (
        <p className="ajuda">Campo em branco não aparece no relatório.</p>
      )}

      {apagando && (
        <Confirmar
          titulo="Apagar este campo de escrita?"
          descricao={
            vazio(anotacao.corpo)
              ? 'O campo está em branco; ele sai do tópico.'
              : 'O campo sai do tópico com tudo o que está nele (texto, tabelas e imagens) e não volta.'
          }
          rotuloAcao="Apagar o campo"
          aoConfirmar={() => void apagar()}
          aoFechar={() => setApagando(false)}
        />
      )}
    </div>
  );
}
